import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Drawer, Spin, Empty } from 'antd';
import { toast } from 'react-toastify';
import axios from 'axios';
import { NdoeError, messageErreur } from '../../components/ndoe';
import config from '../../config';
import { NdoeImage } from '../../components/ndoe';
import { addToPanier } from './panier';
import './quickAddSheet.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/*
  ⑪ Quick-add « façon Mango » — bottom-sheet de sélection de pointure.
  Ouvert depuis le « + » d'une carte OU depuis le tiroir détail. On charge les
  pointures EN STOCK du modèle (par code_variant), et chaque appui sur une
  pointure ajoute une paire au panier (+1) SANS quitter la grille. On résout le
  vrai id_varianteProduit de la pointure choisie (les cartes n'en portent qu'une
  représentative). Feedback discret via react-toastify (standard ⑧⑨⑩).
*/
export default function QuickAddSheet({ open, product, onClose }) {
  const [sizes, setSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);        // id_taille en cours de résolution
  const [added, setAdded] = useState({});         // id_taille -> nombre ajouté dans cette session
  const [noVariant, setNoVariant] = useState(false); // donnée incomplète (pas de code_variant)
  // UNE PANNE N'EST PAS UNE RUPTURE (10/08/2026). Le catch vidait la liste et la
  // feuille affichait « Aucune pointure en stock » : la vendeuse pouvait refuser
  // une vente sur une paire qui était en rayon.
  const [erreur, setErreur] = useState(null);
  // Jeton de session : toute (ré)ouverture ou changement de modèle l'incrémente ;
  // une résolution réseau qui revient avec un jeton périmé est ignorée (pas de
  // toast tardif ni de badge « +N » fantôme collé sur un autre modèle).
  const reqRef = useRef(0);

  const codeVariant = product?.code_variant;

  useEffect(() => {
    const token = ++reqRef.current;   // nouvelle session : invalide les requêtes en vol
    setBusy(null); setSizes([]); setAdded({});
    if (!open) return undefined;
    if (!codeVariant) { setNoVariant(true); setLoading(false); return undefined; }
    setNoVariant(false);
    setErreur(null);
    let cancelled = false;
    setLoading(true);
    axios.get(`${DOMAIN}/api/inventaire/${codeVariant}`)
      .then(({ data }) => {
        if (cancelled || token !== reqRef.current) return;
        const list = (Array.isArray(data) ? data : [])
          .filter((s) => Number(s.nombre_de_paires) !== 0)
          .sort((a, b) => Number(a.taille) - Number(b.taille));
        setSizes(list);
      })
      .catch((e) => {
        if (cancelled || token !== reqRef.current) return;
        setSizes([]);
        setErreur(messageErreur(e, 'les pointures de ce modèle'));
      })
      .finally(() => { if (!cancelled && token === reqRef.current) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, codeVariant]);

  const pick = useCallback(async (size) => {
    if (busy) return;
    const token = reqRef.current;      // session au moment du tap
    setBusy(size.id_taille);
    try {
      // Résolution (code_variant, pointure) -> id_varianteProduit réel (même
      // pipeline que DetailProduitCommande) : la carte ne porte qu'une pointure
      // représentative, on ne peut donc pas réutiliser son id_varianteProduit.
      const { data } = await axios.get(`${DOMAIN}/api/commande/idVariantproduit/${codeVariant}/${size.id_taille}`);
      if (token !== reqRef.current) return; // sheet fermé / modèle changé entre-temps
      const idVariante = data?.[0]?.id_varianteProduit;
      if (!idVariante) { toast.error('Pointure indisponible.'); return; }
      addToPanier({
        id_varianteProduit: idVariante,
        id_taille: size.id_taille,
        id_produit: product.id_produit,
        nom_produit: product.nom_produit,
        marque: product.marque || product.nom_marque,
        taille: size.taille,
        couleur: product.couleur,
        // Prix PROMO s'il est disponible — même correction que Commande.jsx et
        // le wizard. ⚠ Ici la source n'est pas /api/catalogue/stock : le champ
        // peut être absent, auquel cas `??` retombe sur le prix de base et le
        // comportement est inchangé. La route qui alimente cet écran est
        // enrichie côté API dans le même lot.
        prix: Number(product.prix_promo ?? product.prix) || 0,
        qte: 1,
      });
      setAdded((a) => ({ ...a, [size.id_taille]: (a[size.id_taille] || 0) + 1 }));
      toast.success(`${product.nom_produit || 'Article'} · P${size.taille} ajoutée`);
    } catch (e) {
      if (token === reqRef.current) toast.error('Ajout impossible.');
    } finally {
      if (token === reqRef.current) setBusy(null);
    }
  }, [busy, codeVariant, product]);

  return (
    <Drawer
      className="ndoe-qa-sheet"
      placement="bottom"
      open={open}
      onClose={onClose}
      title={null}
      closable={false}
      height="auto"
    >
      {product && (
        <div className="ndoe-qa">
          <div className="ndoe-qa-head">
            <div className="ndoe-qa-thumb">
              <NdoeImage src={product.img} alt={product.nom_produit || 'Article'} size="md" preview={false} />
            </div>
            <div className="ndoe-qa-info">
              <span className="ndoe-qa-marque">{product.marque || product.nom_marque || ' '}</span>
              <b className="ndoe-qa-nom" title={product.nom_produit}>{product.nom_produit}</b>
              {product.prix != null && product.prix !== '' && <span className="ndoe-qa-prix num">{product.prix} $</span>}
            </div>
            <button type="button" className="ndoe-qa-close" onClick={onClose} aria-label="Fermer">✕</button>
          </div>

          <div className="ndoe-qa-label">Choisissez une pointure — chaque appui ajoute une paire</div>

          {loading ? (
            <div className="ndoe-qa-loading"><Spin /></div>
          ) : noVariant ? (
            <Empty description="Détail indisponible pour ce modèle" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : erreur ? (
            <NdoeError
              title="Pointures indisponibles"
              text={erreur}
              onRetry={() => { reqRef.current += 1; setErreur(null); }}
            />
          ) : sizes.length === 0 ? (
            <Empty description="Aucune pointure en stock" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div className="ndoe-qa-sizes">
              {sizes.map((s) => (
                <button
                  key={s.id_taille}
                  type="button"
                  className={`ndoe-qa-size ${added[s.id_taille] ? 'is-added' : ''} ${busy === s.id_taille ? 'is-busy' : ''}`}
                  disabled={busy !== null}
                  onClick={() => pick(s)}
                >
                  <span className="ndoe-qa-size-t">P{s.taille}</span>
                  {added[s.id_taille] > 0 && <span className="ndoe-qa-size-badge">+{added[s.id_taille]}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
